// CelestialFlowerArt.js — flower tributes rendered in the house theme.
// Gold line-art botanicals pressed on parchment, replacing the
// watercolor PNGs. Flower ids (hibiscus, bouquet, chrysanthemum, carnation)
// are preserved, so every tribute ever placed re-renders in the new art.

import React from 'react';

const GOLD = '#b8964f';
const GOLD_SOFT = 'rgba(229,200,131,0.55)';

function FlowerMotif({ type }) {
  const s = { fill: 'none', stroke: GOLD, strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const soft = { ...s, stroke: GOLD_SOFT, strokeWidth: 1.1 };

  switch (type) {
    case 'hibiscus':
      return (
        <svg viewBox="0 0 120 160" className="w-full h-full">
          <path {...s} d="M60 148 C60 122 58 100 58 84" />
          {/* five broad petals */}
          {[0, 72, 144, 216, 288].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 60 62)`}>
              <path {...s} d="M60 62 C48 52 46 34 60 24 C74 34 72 52 60 62 Z" />
              <path {...soft} d="M60 56 C56 48 56 38 60 32" opacity="0.6" />
            </g>
          ))}
          {/* pistil */}
          <path {...s} d="M60 62 C66 52 74 44 84 40" />
          {[[84, 40], [80, 34], [88, 46]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.6" fill={GOLD} stroke="none" />
          ))}
          <path {...soft} d="M60 148 C50 136 40 132 30 134" opacity="0.7" />
        </svg>
      );
    case 'bouquet':
      return (
        <svg viewBox="0 0 120 160" className="w-full h-full">
          {/* stems gathered */}
          <path {...s} d="M60 148 L48 96" /><path {...s} d="M60 148 L60 92" /><path {...s} d="M60 148 L72 96" />
          <path {...s} d="M52 132 C58 128 64 128 68 132" />
          {/* three blooms */}
          {[[48, 84], [60, 74], [72, 84]].map(([x, y], i) => (
            <g key={i}>
              {[0, 60, 120, 180, 240, 300].map((deg) => (
                <ellipse
                  key={deg}
                  cx={x} cy={y - 8} rx="3.4" ry="8"
                  transform={`rotate(${deg} ${x} ${y})`}
                  {...(i === 1 ? s : soft)}
                />
              ))}
              <circle cx={x} cy={y} r="2.2" fill={i === 1 ? GOLD : GOLD_SOFT} stroke="none" />
            </g>
          ))}
          {/* baby's breath stars */}
          {[[36, 66], [84, 66], [60, 54]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.1" fill={GOLD_SOFT} stroke="none" />
          ))}
        </svg>
      );
    case 'chrysanthemum':
      return (
        <svg viewBox="0 0 120 160" className="w-full h-full">
          <path {...s} d="M60 148 C60 124 60 106 60 92" />
          {/* layered thin petals */}
          {Array.from({ length: 16 }, (_, i) => i * 22.5).map((deg) => (
            <path
              key={deg}
              {...soft}
              d="M60 62 C57 48 58 36 60 28 C62 36 63 48 60 62 Z"
              transform={`rotate(${deg} 60 62)`}
            />
          ))}
          {Array.from({ length: 8 }, (_, i) => i * 45 + 11).map((deg) => (
            <path
              key={deg}
              {...s}
              d="M60 62 C58 52 58 44 60 38 C62 44 62 52 60 62 Z"
              transform={`rotate(${deg} 60 62)`}
            />
          ))}
          <circle cx="60" cy="62" r="3" fill={GOLD} stroke="none" />
          <path {...soft} d="M60 120 C70 112 80 110 88 112" opacity="0.7" />
        </svg>
      );
    case 'carnation':
    default:
      return (
        <svg viewBox="0 0 120 160" className="w-full h-full">
          <path {...s} d="M60 148 C60 124 59 104 58 88" />
          {/* ruffled petals */}
          <path {...s} d="M42 70 C38 54 48 40 60 40 C72 40 82 54 78 70 C74 82 66 88 60 88 C54 88 46 82 42 70 Z" />
          <path {...soft} d="M46 66 C50 60 54 58 58 60 M62 58 C66 56 72 58 74 64 M52 76 C56 72 62 72 66 76" />
          <path {...soft} d="M48 48 C52 42 58 40 60 44 M64 42 C68 40 72 44 72 48" opacity="0.7" />
          {/* calyx */}
          <path {...s} d="M54 86 C54 94 56 98 60 100 C64 98 66 94 66 86" />
          <path {...soft} d="M58 118 C48 112 40 112 32 116" opacity="0.7" />
          <circle cx="86" cy="52" r="1.2" fill={GOLD_SOFT} stroke="none" />
        </svg>
      );
  }
}

function CelestialFlowerArt({ type = 'hibiscus', compact = false, name }) {
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #fdfbf6 0%, #faf9f7 100%)' }}
    >
      {/* the faintest warmth beneath the bloom, like light on paper */}
      <div
        className="absolute left-1/2 bottom-0 -translate-x-1/2 pointer-events-none"
        style={{
          width: '72%', height: '34%',
          background: 'radial-gradient(ellipse at 50% 100%, rgba(203,171,106,0.16) 0%, transparent 70%)',
        }}
      />

      <div className={`relative h-full w-full flex items-center justify-center ${compact ? 'p-2 pb-7' : 'p-6 pb-10'}`}>
        <div className={compact ? 'w-3/4 h-full' : 'w-2/3 h-full'}>
          <FlowerMotif type={type} />
        </div>
      </div>

      {!compact && name && (
        <p
          className="absolute bottom-3 left-0 right-0 text-center text-[10px] uppercase pointer-events-none"
          style={{ letterSpacing: '0.3em', color: '#b8964f' }}
        >
          ✦ {name}
        </p>
      )}
    </div>
  );
}

export default CelestialFlowerArt;
